import React, { useCallback, useEffect, useState } from "react";
import _debounce from "lodash/debounce";
import { InputAdornment, Typography, useTheme } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { StyledTextField } from "./DebouncedTextInput";
import { useEmailFieldFeedback } from "../../hooks/useEmailFieldFeedback";

interface DebouncedEmailInputProps {
  label?: string;
  initialValue?: string;
  onChange: (value: string) => void;
  valid?: boolean;
  autoComplete?: string;
  required?: boolean;
}

/**
 * Email variant of {@link DebouncedTextInput}: same look and debounced onChange,
 * but additionally renders an inline error / typo-suggestion / soft-warning
 * message below the field (see {@link useEmailFieldFeedback}). The message logic
 * is shared with the ShadCN flows via the hook, so when those flows migrate
 * away from MUI only this thin wrapper is discarded.
 */
const DebouncedEmailInput = (props: DebouncedEmailInputProps) => {
  const theme = useTheme();
  const [value, setValue] = useState<string>("");
  const [touched, setTouched] = useState<boolean>(false);

  const debouncedOnChange = useCallback(
    _debounce(props.onChange, 700, { leading: false, trailing: true }),
    []
  );

  useEffect(() => {
    if (props.initialValue) {
      setValue(props.initialValue);
    }
  }, [props.initialValue]);

  useEffect(() => {
    debouncedOnChange(value);
  }, [value]);

  const feedback = useEmailFieldFeedback(value, touched);
  const showValid = props.valid && value.trim() !== "";

  const messageBaseSx = {
    fontSize: "0.75rem",
    paddingLeft: "13px",
    marginTop: "-12px",
    marginBottom: "12px",
  } as const;

  return (
    <>
      <StyledTextField
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onBlur={() => setTouched(true)}
        variant={"outlined"}
        label={props.label}
        color={"primary"}
        fullWidth={false}
        size={"medium"}
        required={props.required ?? true}
        autoComplete={props.autoComplete}
        InputLabelProps={{
          style: { color: "rgba(0, 0, 0, 0.8)" },
        }}
        InputProps={{
          endAdornment: showValid ? (
            <InputAdornment position="end">
              <CheckIcon sx={{ color: theme.palette.input.focus }} />
            </InputAdornment>
          ) : null,
        }}
        valid={showValid}
      />
      {feedback.error && (
        <Typography
          role="alert"
          sx={{ ...messageBaseSx, color: theme.palette.error.main }}
        >
          {feedback.error}
        </Typography>
      )}
      {feedback.warning && (
        <Typography
          role="status"
          sx={{ ...messageBaseSx, color: theme.palette.text.secondary }}
        >
          {feedback.warning}
        </Typography>
      )}
    </>
  );
};

export default DebouncedEmailInput;
